import { inject, Injectable } from '@angular/core';
import { assert } from '@sama/js-core';
import { InjectableSuperclass } from '@sama/ng-core';
import { BehaviorSubject, combineLatest, Observable, switchMap } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { AbstractAnnotationStore } from '../../../../../outputs/annotation/annotation-shared/state/root/abstract-annotation-store';
import {
  InteractionMode,
  InteractionRestrictionType,
} from '../../../../../team-apis/core/interaction-modes';
import { outputInfoStoreToken } from '../../../../h3ml/state/output-info';
import { TaskWorkspaceStore } from '../../../../h3ml/state/task-workspace-store';
import { FusionManager } from '../fusion/fusion-manager';

/**
 * InteractionModeService is used to reserve a specific set of interactions.
 * For example, the interaction mode is used for setting or disabling keyboard shortcuts.
 *
 * The one reserving the interaciton mode is also responsible for releasing it. Different parts of the code
 * can reserve interaction modes. The most restrictive modes have higher priority when multiple modes
 * are applied simultaneously.
 *
 */
@Injectable()
export class InteractionModeService extends InjectableSuperclass {
  // All values should be read with isInMode and all interaction changes
  // should happen through restrictInteractions.
  // This can be set to private when the KeyboardShortcutService starts using
  // isInMode$.
  interactionMode!: InteractionMode;
  private locks$ = new BehaviorSubject({ none: 0, canvas: 0 });
  private interactionMode$: Observable<InteractionMode>;
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private fusionManager = inject(FusionManager);
  private outputInfoStore = inject(outputInfoStoreToken);

  constructor(store: AbstractAnnotationStore) {
    super();
    this.interactionMode$ = combineLatest([
      this.locks$,
      store('status').$,
      this.buildFrameIsDownloading$(),
    ]).pipe(
      map(([locks, status, frameIsDownloading]) => {
        if (locks.none || status === 'loading') {
          return 'none';
        } else if (locks.canvas) {
          return 'canvas';
        } else if (frameIsDownloading) {
          return 'scrubber';
        } else {
          return 'all';
        }
      }),
      distinctUntilChanged(),
    );
    this.subscribeTo(this.interactionMode$, (mode) => {
      this.interactionMode = mode;
    });
  }

  isInMode$(mode: InteractionMode) {
    return this.interactionMode$.pipe(map((current) => current === mode));
  }

  restrictInteractions(mode: InteractionRestrictionType) {
    this.updateLock(mode, 1);
    let released = false;
    return () => {
      assert(!released, 'only call release once');
      this.updateLock(mode, -1);
      released = true;
    };
  }

  private updateLock(mode: InteractionRestrictionType, delta: number) {
    const locks = this.locks$.value;
    this.locks$.next({ ...locks, [mode]: locks[mode] + delta });
  }

  private buildFrameIsDownloading$() {
    const outputName = this.outputInfoStore.state().output.name;
    return this.fusionManager.getFrameNumberForOutput$(outputName).pipe(
      switchMap(
        (frameNumber) =>
          this.taskWorkspaceStore.getLoadedFramesStore(outputName)(frameNumber)
            .$,
      ),
      map((loadedFrame) => !loadedFrame),
    );
  }
}
